import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Flower2, Users, Sparkles, Heart, Clock, Lock, UserRound, Star, Search } from "lucide-react";

const LOGO = "/upwardcast-logo-transparent.png";
const HERO_IMG =
  "https://images.unsplash.com/photo-1464207687429-7505649dae38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxncm91cCUyMG1lZGl0YXRpb24lMjBnYXRoZXJpbmclMjBzdW5zZXR8ZW58MHx8fHwxNzgyMDY2NzgwfDA&ixlib=rb-4.1.0&q=85";
const COMMUNITY_IMG =
  "https://images.unsplash.com/photo-1529182856268-587c45053e0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxncm91cCUyMG1lZGl0YXRpb24lMjBnYXRoZXJpbmclMjBzdW5zZXR8ZW58MHx8fHwxNzgyMDY2NzgwfDA&ixlib=rb-4.1.0&q=85";

function durationLabel(s) {
  const m = Math.round(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60 ? `${m % 60}m` : ""}`.trim() : `${m} min`;
}
const modeLabel = (mode) => (mode === "live" ? "Live gathering" : "Anytime experience");

export default function Landing() {
  const [experiences, setExperiences] = useState([]);
  const [q, setQ] = useState("");
  const [hostFilter, setHostFilter] = useState("");
  const [sort, setSort] = useState("default");

  useEffect(() => {
    api.get("/public/webinars").then((r) => setExperiences(r.data)).catch(() => {});
  }, []);

  const allHosts = Array.from(
    new Set(experiences.flatMap((w) => (w.hosts || []).map((h) => (h.name || "").trim()).filter(Boolean)))
  ).sort((a, b) => a.localeCompare(b));

  const s = q.trim().toLowerCase();
  let filtered = experiences.filter((w) => {
    const hostNames = (w.hosts || []).map((h) => h.name || "");
    if (hostFilter && !hostNames.includes(hostFilter)) return false;
    if (!s) return true;
    return `${w.title} ${w.description} ${hostNames.join(" ")}`.toLowerCase().includes(s);
  });
  if (sort === "price-asc") filtered = [...filtered].sort((a, b) => (a.price || 0) - (b.price || 0));
  else if (sort === "price-desc") filtered = [...filtered].sort((a, b) => (b.price || 0) - (a.price || 0));

  return (
    <div className="wellness min-h-screen bg-[#FBFAFF]" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#FBFAFF]/80 border-b border-[#E5E0F5]">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-logo">
            <img src={LOGO} alt="UpwardCast" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-5">
            <a href="#experiences" className="hidden sm:inline text-sm text-[#6A6588] hover:text-[#211C36] transition-colors">
              Experiences
            </a>
            <Link
              to="/admin/login"
              data-testid="nav-admin-link"
              className="hidden sm:flex text-sm text-[#6A6588] hover:text-[#211C36] transition-colors items-center gap-1.5"
            >
              <Lock className="w-3.5 h-3.5" /> Host login
            </Link>
            <Link
              to="/account"
              data-testid="nav-account-link"
              className="text-sm font-medium px-4 py-2 rounded-full border border-[#7B2CBF]/30 text-[#7B2CBF] hover:bg-[#7B2CBF]/8 transition-colors flex items-center gap-1.5"
            >
              <UserRound className="w-3.5 h-3.5" /> My account
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#A78BFA]/25 blur-3xl animate-float-slow" />
        <div className="absolute top-40 -left-20 w-72 h-72 rounded-full bg-[#16B6A4]/15 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#7B2CBF] mb-7">
              <Sparkles className="w-3.5 h-3.5" /> Live & on-demand · Together
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.02] mb-7">
              Gather, breathe,<br />
              <span className="italic text-gradient">grow together.</span>
            </h1>
            <p className="text-lg sm:text-xl text-[#6A6588] leading-relaxed max-w-lg mb-9">
              Intimate live and on-demand experiences with the guides you love. Find your people,
              slow down, and make space for what matters.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#experiences"
                data-testid="hero-cta-browse"
                className="group btn-gradient text-white px-8 py-4 rounded-full font-medium flex items-center gap-2 soft-shadow-sm hover:-translate-y-0.5"
              >
                Explore experiences
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" />
              </a>
              <div className="flex items-center gap-2 text-sm text-[#6A6588]">
                <Heart className="w-4 h-4 text-[#16B6A4]" /> Loved by a warm community
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative">
              <div className="overflow-hidden rounded-[2rem] soft-shadow">
                <img src={HERO_IMG} alt="A community gathering at sunset" className="w-full h-[460px] object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-3xl p-5 soft-shadow-sm flex items-center gap-3 max-w-[15rem]">
                <div className="w-11 h-11 rounded-full bg-[#7B2CBF]/12 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#7B2CBF]" />
                </div>
                <div>
                  <div className="font-display text-xl leading-none">Join live</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value strip */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Clock, color: "#7B2CBF", title: "On your time", body: "Drop into sessions that begin just as you arrive — no waiting, no pressure." },
            { icon: Users, color: "#16B6A4", title: "A true community", body: "Share the room with kindred spirits and a chat that feels supportive, not noisy." },
            { icon: Sparkles, color: "#2563EB", title: "Guided by the best", body: "Learn and unwind with hosts who bring warmth, depth, and care." },
          ].map((f, i) => (
            <div key={i} className="bg-[#F1EEFB] rounded-3xl p-8 transition-transform duration-500 hover:-translate-y-1">
              <f.icon className="w-6 h-6 mb-4" style={{ color: f.color }} strokeWidth={1.5} />
              <h3 className="text-2xl mb-2">{f.title}</h3>
              <p className="text-[#6A6588] leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Experiences */}
      <section id="experiences" className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-[0.25em] text-[#7B2CBF] mb-4">Upcoming</div>
          <h2 className="text-4xl sm:text-5xl font-light">Find your next gathering</h2>
          <div className="relative mt-8 max-w-md mx-auto">
            <Search className="w-4 h-4 text-[#9A95B5] absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search experiences, guides, topics…"
              data-testid="landing-search-input"
              className="w-full bg-white border border-[#E5E0F5] rounded-full pl-11 pr-4 py-3.5 text-sm placeholder-[#9A95B5] focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]/40 soft-shadow-sm"
            />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#9A95B5]">Host</span>
              <select value={hostFilter} onChange={(e) => setHostFilter(e.target.value)} data-testid="landing-host-filter" className="bg-white border border-[#E5E0F5] rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]/40">
                <option value="">All hosts</option>
                {allHosts.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#9A95B5]">Sort</span>
              <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="landing-sort" className="bg-white border border-[#E5E0F5] rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7B2CBF]/40">
                <option value="default">Featured</option>
                <option value="price-asc">Price: low to high</option>
                <option value="price-desc">Price: high to low</option>
              </select>
            </div>
            {(hostFilter || sort !== "default" || q) && (
              <button onClick={() => { setQ(""); setHostFilter(""); setSort("default"); }} data-testid="landing-clear-filters" className="text-xs text-[#7B2CBF] font-medium underline underline-offset-2">
                Clear
              </button>
            )}
          </div>
        </div>

        {experiences.length === 0 ? (
          <div className="bg-[#F1EEFB] rounded-3xl p-16 text-center" data-testid="no-webinars">
            <Flower2 className="w-8 h-8 text-[#7B2CBF] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#6A6588]">New experiences are being prepared with care. Check back soon.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-[#F1EEFB] rounded-3xl p-16 text-center" data-testid="search-no-results">
            <Search className="w-8 h-8 text-[#7B2CBF] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#6A6588]">No experiences match your filters. Try adjusting your search, host, or sort.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="webinar-grid">
            {filtered.map((w, i) => (
              <Link
                to={`/w/${w.slug}`}
                key={w.id}
                data-testid={`webinar-card-${w.slug}`}
                className="group bg-white rounded-[1.75rem] overflow-hidden soft-shadow-sm transition-all duration-500 hover:-translate-y-1.5 flex flex-col animate-fade-up"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="relative h-52 overflow-hidden bg-[#E5E0F5]">
                  <img
                    src={w.image_url || COMMUNITY_IMG}
                    alt={w.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-xs font-medium text-[#7B2CBF]">
                    {modeLabel(w.mode)}
                  </div>
                  <div className="absolute top-4 right-4 btn-gradient text-white px-3 py-1.5 rounded-full text-xs font-semibold">
                    {w.price > 0 ? `$${w.price.toFixed(0)}` : "Free"}
                  </div>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="text-xs uppercase tracking-wider text-[#16B6A4]">{durationLabel(w.duration_seconds)}</div>
                    {w.rating_count > 0 && (
                      <div className="flex items-center gap-1 text-xs font-medium text-[#6A6588]" data-testid={`card-rating-${w.slug}`}>
                        <Star className="w-3.5 h-3.5 fill-[#F2B705] text-[#F2B705]" />
                        {w.rating_average} <span className="text-[#B8B3CC]">({w.rating_count})</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-2xl leading-tight mb-3">{w.title}</h3>
                  <p className="text-[#6A6588] leading-relaxed line-clamp-2 flex-1">{w.description}</p>
                  <div className="mt-5 flex items-center gap-2 text-[#7B2CBF] font-medium">
                    Reserve your spot
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Community band */}
      <section className="relative overflow-hidden brand-gradient">
        <div className="max-w-6xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl sm:text-5xl font-light text-white mb-5">
              More than a session — a place to belong.
            </h2>
            <p className="text-lg text-white/85 leading-relaxed mb-8 max-w-md">
              Every experience is a doorway into a community that shows up for one another.
              Come as you are. Leave a little lighter.
            </p>
            <a
              href="#experiences"
              className="inline-flex items-center gap-2 bg-white text-[#211C36] hover:bg-[#F1EEFB] px-8 py-4 rounded-full font-medium transition-all duration-500 hover:-translate-y-0.5"
            >
              Reserve your spot <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="overflow-hidden rounded-[2rem] shadow-2xl">
            <img src={COMMUNITY_IMG} alt="Community at sunset" className="w-full h-80 object-cover" />
          </div>
        </div>
      </section>

      <footer className="bg-[#FBFAFF] py-14 border-t border-[#E5E0F5]">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={LOGO} alt="UpwardCast" className="h-7 w-auto" />
          <div className="text-sm text-[#6A6588]">Experiences for a fuller life.</div>
        </div>
      </footer>
    </div>
  );
}
